import { useMutation } from "@apollo/client";
import { TextField } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { gql } from "generated-graphql";
import { CersRejectionResolutionType } from "generated-graphql/graphql";
import { useState } from "react";

export const ACKNOWLEDGE_CERS_REJECTION_FINDING = gql(`
  mutation AcknowledgeCersRejectionFindingFromDrawer(
    $id: ID!
    $resolutionType: CersRejectionResolutionType!
    $note: String
  ) {
    resolveCersRejectionFinding(id: $id, resolutionType: $resolutionType, note: $note) {
      id
      status
      resolutionType
      note
      resolvedAt
      history {
        id
        eventType
        status
        resolutionType
        note
        actorName
        createdAt
      }
    }
  }
`);

type Props = {
  open: boolean;
  findingId: string;
  onClose: () => void;
  /** Called after a successful acknowledgement (dialog closes itself first). */
  onDone: () => void;
};

/** Acknowledge: resolved without a revision (corrected elsewhere / no action needed). */
export function AcknowledgeDialog({ open, findingId, onClose, onDone }: Props) {
  const [note, setNote] = useState("");
  const alerts = useAlerts();

  const [resolve, { loading }] = useMutation(ACKNOWLEDGE_CERS_REJECTION_FINDING, {
    refetchQueries: ["StaffCersRejectionFindings", "CersFindingDetail"],
    // Hold the dialog's loading state until the drawer + grid actually show
    // the new status — without this the dialog closes ~1-2s before the chip
    // flips, which reads as "nothing happened".
    awaitRefetchQueries: true,
  });

  const handleClose = () => {
    setNote("");
    onClose();
  };

  const handleSubmit = async () => {
    try {
      await resolve({
        variables: {
          id: findingId,
          resolutionType: CersRejectionResolutionType.Acknowledged,
          note: note.trim() || undefined,
        },
      });
      alerts.success("Finding marked resolved.");
      handleClose();
      onDone();
    } catch (e) {
      alerts.error("Failed to mark the finding resolved.", e);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      slotProps={{ paper: { sx: { width: 560, maxWidth: "90vw" } } }}
      title="Mark finding resolved"
      description="Acknowledge this rejection as corrected elsewhere or needing no action — no revision will be filed."
      primaryActionButtonProps={{
        onClick: handleSubmit,
        loading,
        loadingText: "Saving...",
        hideStartIcon: true,
        saveText: "Mark resolved",
        disabled: loading,
      }}
      cancelButtonProps={{ disabled: loading }}
    >
      <TextField
        label="Note (optional)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        size="small"
        multiline
        minRows={2}
        fullWidth
        autoFocus
        sx={{ mt: 1 }}
      />
    </Dialog>
  );
}
